import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Loader2, FileText, TrendingUp, TrendingDown, Package, Target, AlertTriangle, CheckCircle2, Download } from 'lucide-react';
import { formatCurrency, cn } from '../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WeeklyReportPage() {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API}/reports/weekly`);
      setReport(res.data);
    } catch (error) {
      console.error('Failed to fetch weekly report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export/weekly-report`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `weekly_report_${report?.week_start || 'report'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="weekly-report-page">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
              Weekly Product Mix Report
            </h1>
            {report && (
              <p className="text-slate-600 mt-1">
                {new Date(report.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {new Date(report.week_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            )}
          </div>
          {report && report.reports.length > 0 && (
            <Button
              onClick={handleExportPDF}
              disabled={exporting}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="export-pdf-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export PDF
            </Button>
          )}
        </div>

        {report && report.reports.length > 0 ? (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-slate-500">Total Locations</p>
                  <p className="text-2xl font-bold font-mono">{report.reports.length}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-slate-500">On Target</p>
                  <p className="text-2xl font-bold font-mono text-emerald-600">
                    {report.reports.filter(r => r.status === 'on_target').length}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-slate-500">Below Target</p>
                  <p className="text-2xl font-bold font-mono text-amber-600">
                    {report.reports.filter(r => r.status === 'below_target').length}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-slate-500">Avg Supplemental %</p>
                  <p className="text-2xl font-bold font-mono">
                    {(report.reports.reduce((sum, r) => sum + r.product_mix.supplemental_pct, 0) / report.reports.length).toFixed(1)}%
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Location Reports */}
            {report.reports.map((locReport, index) => (
              <Card key={locReport.location_id} data-testid={`report-${locReport.location_id}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <span className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-violet-700 font-bold text-sm">
                          {index + 1}
                        </span>
                        {locReport.location_name}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {locReport.entry_count} entries this week
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge 
                        className={cn(
                          locReport.status === 'on_target' 
                            ? 'bg-emerald-500' 
                            : 'bg-amber-500'
                        )}
                      >
                        {locReport.status === 'on_target' ? (
                          <><CheckCircle2 className="w-3 h-3 mr-1" /> On Target</>
                        ) : (
                          <><AlertTriangle className="w-3 h-3 mr-1" /> Below Target</>
                        )}
                      </Badge>
                      <div className={cn(
                        "w-14 h-14 rounded-xl flex items-center justify-center font-mono font-bold text-lg text-white",
                        locReport.summary.average_score >= 85 ? 'bg-emerald-500' :
                        locReport.summary.average_score >= 70 ? 'bg-lime-500' :
                        locReport.summary.average_score >= 55 ? 'bg-amber-500' : 'bg-red-500'
                      )}>
                        {Math.round(locReport.summary.average_score)}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Summary */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Week Summary
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 rounded-lg bg-slate-50">
                          <p className="text-xs text-slate-500">Total Sales</p>
                          <p className="font-mono font-semibold">{formatCurrency(locReport.summary.total_sales)}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-50">
                          <p className="text-xs text-slate-500">Budget Attainment</p>
                          <p className={cn(
                            "font-mono font-semibold",
                            locReport.summary.budget_attainment >= 100 ? 'text-emerald-600' : 'text-amber-600'
                          )}>
                            {locReport.summary.budget_attainment}%
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Product Mix */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Product Mix Performance
                      </h4>
                      <div className="space-y-3">
                        {/* Supplemental % */}
                        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                          <div>
                            <p className="text-xs text-slate-500">Supplemental Sales %</p>
                            <p className={cn(
                              "font-mono font-semibold text-lg",
                              locReport.product_mix.supplemental_pct >= locReport.product_mix.supplemental_target 
                                ? 'text-emerald-600' 
                                : 'text-amber-600'
                            )}>
                              {locReport.product_mix.supplemental_pct}%
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Target: {locReport.product_mix.supplemental_target}%</p>
                            <p className={cn(
                              "text-sm font-semibold",
                              locReport.product_mix.supplemental_vs_target >= 0 ? 'text-emerald-600' : 'text-red-600'
                            )}>
                              {locReport.product_mix.supplemental_vs_target >= 0 ? '+' : ''}{locReport.product_mix.supplemental_vs_target}%
                            </p>
                          </div>
                        </div>

                        {/* Digital vs Hard */}
                        <div className="p-3 rounded-lg bg-slate-50">
                          <div className="flex justify-between mb-2">
                            <span className="text-xs text-slate-500">Hard vs Digital Mix</span>
                            <span className="text-xs text-slate-500">Target Digital: {locReport.product_mix.digital_target}%</span>
                          </div>
                          <div className="flex h-4 rounded-full overflow-hidden bg-slate-200">
                            <div 
                              className="bg-orange-400 transition-all"
                              style={{ width: `${locReport.product_mix.hard_mix_pct}%` }}
                            />
                            <div 
                              className="bg-blue-500 transition-all"
                              style={{ width: `${locReport.product_mix.digital_mix_pct}%` }}
                            />
                          </div>
                          <div className="flex justify-between mt-2 text-xs">
                            <span className="text-orange-600 font-medium">Hard: {locReport.product_mix.hard_mix_pct}%</span>
                            <span className="text-blue-600 font-medium">Digital: {locReport.product_mix.digital_mix_pct}%</span>
                          </div>
                        </div>

                        {/* Supplemental per labor hour */}
                        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                          <div>
                            <p className="text-xs text-slate-500">Supplemental $/Labor Hour</p>
                            <p className="font-mono font-semibold">${locReport.product_mix.supplemental_per_labor_hour}</p>
                          </div>
                          <div className="text-right text-xs text-slate-500">
                            <p>Core: {formatCurrency(locReport.product_mix.core_photo_sales)}</p>
                            <p>Supp: {formatCurrency(locReport.product_mix.supplemental_sales)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No data for this week</h3>
              <p className="text-slate-500">Weekly reports will appear once entries are submitted</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
